import { Badge } from '@/components/ui/badge';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { z } from 'zod';
import { FormSection, MasterDataForm } from '../components/master-data/MasterDataForm';
import { FilterConfig, MasterDataTable, TableColumn } from '../components/master-data/MasterDataTable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { getStateService } from '../services/stateService';
import { State, StateFormData } from '../types/state';

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const StateManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingState, setEditingState] = useState<State | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();
  const stateService = getStateService(axios);

  // State for server-side pagination, filtering, and searching
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<{ sortBy?: string; sortOrder?: 'asc' | 'desc' }>({});

  // Fetch states with server-side filtering and pagination (like Hub management)
  const {
    data: statesResponse,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['states', currentPage, pageSize, searchTerm, currentFilters, sortConfig],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });
      
      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(`/api/master-data/state?${params.toString()}`);
      const result = response.data.data || {};
      console.log('State API response:', result);
      
      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1
        }
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Fetch countries for dropdown (exactly like Hub management)
  const { data: countries = [] } = useQuery({
    queryKey: ['countries-dropdown'],
    queryFn: async () => {
      const response = await axios.get('/api/master-data/country');
      console.log('StateManagement - Countries API Response:', response.data);
      const countriesData = response.data.data?.data?.map((country: any) => ({
        value: country._id, // Use country ID as value for State master (different from Hub)
        label: `${country.name} (${country.code})`
      })) || [];
      console.log('StateManagement - Countries for dropdown:', countriesData);
      return countriesData;
    },
  });

  const states = statesResponse?.data || [];
  const pagination = statesResponse?.pagination;

  const countryOptions = countries;

  // Show debugging info
  React.useEffect(() => {
    console.log('States API Response:', statesResponse);
    console.log('States data:', states);
    console.log('States pagination:', pagination);
    console.log('States length:', states.length);
    console.log('Is states array?', Array.isArray(states));
    console.log('Countries data:', countries);
    console.log('Country options:', countryOptions);
    
    // Check individual state object
    if (states.length > 0) {
      console.log('First state object:', states[0]);
      console.log('State keys:', Object.keys(states[0]));
    }
  }, [statesResponse, states, pagination, countries, countryOptions]);

  // Create state mutation
  const createStateMutation = useMutation({
    mutationFn: (data: StateFormData) => stateService.createState(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['states'] });
      toast({
        title: 'Success',
        description: 'State created successfully',
      });
      setIsFormOpen(false);
      setEditingState(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      console.error('Create state error:', error);
      const errorData = error.response?.data?.error;
      const errorMessage = typeof errorData === 'object' 
        ? (errorData?.message || JSON.stringify(errorData)) 
        : (errorData || error.message || 'Failed to create state');
      const validationErrors = error.response?.data?.errors || {};
      
      setServerErrors(validationErrors);
      toast({
        title: 'Error',
        description: String(errorMessage),
        variant: 'destructive',
      });
    },
  });

  // Update state mutation
  const updateStateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<StateFormData> }) =>
      stateService.updateState(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['states'] });
      toast({
        title: 'Success',
        description: 'State updated successfully',
      });
      setIsFormOpen(false);
      setEditingState(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      console.error('Update state error:', error);
      const errorData = error.response?.data?.error;
      const errorMessage = typeof errorData === 'object' 
        ? (errorData?.message || JSON.stringify(errorData)) 
        : (errorData || error.message || 'Failed to update state');
      const validationErrors = error.response?.data?.errors || {};
      
      setServerErrors(validationErrors);
      toast({
        title: 'Error',
        description: String(errorMessage),
        variant: 'destructive',
      });
    },
  });

  // Delete state mutation
  const deleteStateMutation = useMutation({
    mutationFn: (id: string) => stateService.deleteState(id, true), // Soft delete
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['states'] });
      toast({
        title: 'Success',
        description: 'State deleted successfully',
      });
    },
    onError: (error: any) => {
      console.error('Delete state error:', error);
      const errorData = error.response?.data?.error;
      const errorMessage = typeof errorData === 'object' 
        ? (errorData?.message || JSON.stringify(errorData)) 
        : (errorData || error.message || 'Failed to delete state');
      toast({
        title: 'Error',
        description: String(errorMessage),
        variant: 'destructive',
      });
    },
  });

  // Table columns (fixed to match MasterDataTable expectations)
  const columns: TableColumn<State>[] = [
    {
      key: 'name',
      label: 'State Name',
      sortable: true,
      render: (value, item) => (
        <div className="font-medium">
          {item.name}
        </div>
      ),
    },
    {
      key: 'code',
      label: 'State Code',
      sortable: true,
      render: (value, item) => (
        <div className="font-mono text-sm bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
          {item.code}
        </div>
      ),
    },
    {
      key: 'portCode',
      label: 'Port Code',
      sortable: true,
      hideOnMobile: true, // Hide on mobile to save space
      render: (value, item) => (
        <div className="font-mono text-sm">
          {item.portCode || '-'}
        </div>
      ),
    },
    {
      key: 'gstStateCode',
      label: 'GST Code',
      sortable: true,
      hideOnMobile: true, // Hide on mobile to save space
      render: (value, item) => (
        item.gstStateCode ? (
          <div className="font-mono text-sm bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded">
            {item.gstStateCode}
          </div>
        ) : (
          <div className="font-mono text-sm text-gray-400">-</div>
        )
      ),
    },
    {
      key: 'country',
      label: 'Country',
      sortable: true,
      hideOnMobile: true, // Hide on mobile, show in card view
      render: (value, item) => {
        const country = typeof item.country === 'object' ? item.country : null;
        return (
          <div className="text-sm">
            {country?.name || 'N/A'}
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value, item) => (
        <Badge variant={item.status === 'Active' ? 'default' : 'secondary'}>
          {item.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      hideOnMobile: true, // Hide on mobile to save space
      hideOnTablet: true, // Also hide on tablet
      render: (value, item) => (
        <div className="text-sm text-gray-500">
          {new Date(item.createdAt).toLocaleDateString()}
        </div>
      ),
    },
  ];

  // Filters configuration
  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: statusOptions,
      placeholder: 'All Statuses',
    },
    {
      key: 'country',
      label: 'Country',
      type: 'select',
      options: countryOptions,
      placeholder: 'All Countries',
    },
  ];

  // Enhanced filter config with search capabilities
  const enhancedFilterConfig = filters.map(filter => ({
    ...filter,
    searchable: filter.key === 'country', // Make country dropdown searchable
  }));

  // Form validation schema
  const stateSchema = z.object({
    name: z.string().min(2, 'State name must be at least 2 characters').max(100, 'State name cannot exceed 100 characters'),
    code: z.string().min(2, 'State code must be at least 2 characters').max(10, 'State code cannot exceed 10 characters').regex(/^[A-Z0-9_-]+$/, 'Code can only contain uppercase letters, numbers, underscores and hyphens'),
    portCode: z.string().max(10, 'Port code cannot exceed 10 characters').regex(/^[A-Z0-9]*$/, 'Port code can only contain uppercase letters and numbers').optional().or(z.literal('')),
    country: z.string().min(1, 'Country is required'),
    gstStateCode: z.string().regex(/^([0-9]{2})?$/, 'GST State code must be exactly 2 digits').optional().or(z.literal('')),
    status: z.enum(['Active', 'Inactive'], { required_error: 'Status is required' }),
  });

  // Form sections
  const formSections: FormSection[] = [
    {
      title: 'Basic Information',
      description: 'Enter the basic details of the state',
      fields: [
        {
          name: 'name',
          label: 'State Name',
          type: 'text',
          required: true,
          placeholder: 'Enter state name',
          validation: stateSchema.shape.name,
        },
        {
          name: 'code',
          label: 'State Code',
          type: 'text',
          required: true,
          placeholder: 'Enter unique state code (e.g., MH, DL)',
          validation: stateSchema.shape.code,
        },
        {
          name: 'portCode',
          label: 'Port Code',
          type: 'text',
          required: false,
          placeholder: 'Enter port code (e.g., INMAA, INDEL) - Optional',
          validation: stateSchema.shape.portCode,
        },
      ],
    },
    {
      title: 'Location & Classification',
      description: 'Specify the country and classification details',
      fields: [
        {
          name: 'country',
          label: 'Country',
          type: 'select',
          required: true,
          options: countryOptions,
          placeholder: 'Select country',
          validation: stateSchema.shape.country,
        },
        {
          name: 'gstStateCode',
          label: 'GST State Code',
          type: 'text',
          required: false,
          placeholder: 'Enter 2-digit GST state code (e.g., 27, 07) - Optional',
          validation: stateSchema.shape.gstStateCode,
        },
      ],
    },
    {
      title: 'Status',
      description: 'Set the operational status of the state',
      fields: [
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: statusOptions,
          validation: stateSchema.shape.status,
        },
      ],
    },
  ];

  // Event handlers
  const handleCreate = () => {
    setEditingState(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (state: State) => {
    setEditingState(state);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = async (state: State) => {
    if (window.confirm(`Are you sure you want to delete the state "${state.name}"?`)) {
      deleteStateMutation.mutate(state._id);
    }
  };

  const handleFormSubmit = async (data: any) => {
    try {
      if (editingState) {
        await updateStateMutation.mutateAsync({
          id: editingState._id,
          data: data as StateFormData,
        });
      } else {
        await createStateMutation.mutateAsync(data as StateFormData);
      }
    } catch (error) {
      // Error handling is done in the mutation's onError callback
      console.error('Form submission error:', error);
    }
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['states'] });
  };

  // Export functionality
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      
      // Add current filters to export
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });

      const result = await stateService.exportStates(currentFilters);
      
      if (result.success && result.data) {
        // Create CSV content
        const headers = ['Name', 'Code', 'Port Code', 'Country', 'GST State Code', 'Status', 'Created At'];
        const csvContent = [
          headers.join(','),
          ...result.data.map((state: State) => [
            `"${state.name}"`,
            state.code,
            state.portCode || '',
            `"${typeof state.country === 'object' ? state.country.name : 'N/A'}"`,
            state.gstStateCode || '',
            state.status,
            new Date(state.createdAt).toLocaleDateString()
          ].join(','))
        ].join('\n');

        // Download CSV
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `states-export-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast({
          title: 'Export successful',
          description: `Exported ${result.data.length} state records`,
        });
      } else {
        toast({
          title: 'Export failed',
          description: result.message || 'Failed to export states',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Export error:', error);
      const errorData = error.response?.data?.error;
      const errorMessage = typeof errorData === 'object' 
        ? (errorData?.message || JSON.stringify(errorData)) 
        : (errorData || error.message || 'Failed to export states');
      toast({
        title: 'Export failed',
        description: String(errorMessage),
        variant: 'destructive',
      });
    }
  };

  // Transform editing state data for form
  const transformedEditingState = editingState ? {
    ...editingState,
    country: typeof editingState.country === 'object' ? editingState.country._id : editingState.country,
  } : null;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <MasterDataTable
          title="State Management"
          description="Manage states, port codes, and GST state classifications"
          data={states}
          columns={columns}
          isLoading={isLoading}
          onAdd={handleCreate}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRefresh={handleRefresh}
          onExport={handleExport}
          searchPlaceholder="Search states by name, code, port code, or GST state code..."
          filters={enhancedFilterConfig}
          allowBulkOperations={false}
          allowImportExport={true}
          // Server-side filtering and pagination props
          serverSide={true}
          pagination={pagination}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          onSearchChange={setSearchTerm}
          onFiltersChange={setCurrentFilters}
          onSortChange={setSortConfig}
          currentSearch={searchTerm}
          currentFilters={currentFilters}
          currentSort={sortConfig}
        />

        <MasterDataForm
          title={editingState ? 'Edit State' : 'Add New State'}
          description={
            editingState
              ? 'Update state information, port code, and GST classification.'
              : 'Create a new state with port code and GST state classification.'
          }
          sections={formSections}
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setEditingState(null);
            setServerErrors({});
          }}
          onSubmit={handleFormSubmit}
          isLoading={createStateMutation.isPending || updateStateMutation.isPending}
          initialData={transformedEditingState}
          mode={editingState ? 'edit' : 'create'}
          serverErrors={serverErrors}
          entityName="State"
        />
      </div>
    </div>
  );
};

export default StateManagement;
